import { PropsWithChildren, useEffect, useMemo } from 'react';
import {
  basicLogger,
  LDContext,
  LDEvaluationDetail,
  LDMultiKindContext,
  LDProvider,
  useLDClient,
} from 'launchdarkly-react-client-sdk';
import { z } from 'zod';

import { useAppQuery } from '@queries/app';
import { useCurrentTeam, useUserQuery } from '@queries/user';
import { monitoring } from '@services/monitoring';
import { ENV } from '@utils/env';
import { useStrongParams } from '@utils/params';

const ldConfigParams = z.object({
  teamUuid: z.string(),
  appUuid: z.string().optional(),
});

function useLDIdentifyContext(): LDContext {
  const { teamUuid, appUuid } = useStrongParams(ldConfigParams);
  const { data: user } = useUserQuery();
  const { team } = useCurrentTeam();
  const { data: app } = useAppQuery(teamUuid, appUuid);

  return useMemo(() => {
    if (!user) {
      return {
        kind: 'consumer',
        anonymous: true,
      };
    }

    const context: LDMultiKindContext = {
      kind: 'multi',
      consumer: {
        key: String(user.id),
        name: user.name,
      },
    };

    if (team) {
      /**
       * @deprecated
       * Keep for backwards compatibility with existing LaunchDarkly configuration.
       */
      context.user = {
        key: `team-${team.uuid}-user-${user.id}`,
        name: user.name,
        team: `team-${team.uuid}`,
        meta: {
          deprecated: true,
        },
      };

      context.team = {
        key: team.uuid,
        name: team.name,
      };
    }

    if (app) {
      context.app = {
        key: app.uuid,
        name: app.name,
      };
    }

    return context;
  }, [user, teamUuid, team, app]);
}

export function LaunchDarklySubscriber() {
  const ld = useLDClient();
  const context = useLDIdentifyContext();

  useEffect(() => {
    void ld?.identify(context);
  }, [ld, context]);

  return null;
}

function useTestFlags() {
  if (ENV.dev || ENV.mode === 'test') {
    return window.__FLAGS__;
  }
}

export function LaunchDarklyProvider({ children }: PropsWithChildren) {
  const testFlags = useTestFlags();

  return (
    <LDProvider
      flags={testFlags}
      clientSideID={ENV.launchDarkly.clientId}
      options={{
        logger: basicLogger({ level: 'none' }),
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key: string, detail: LDEvaluationDetail) => {
              monitoring.observeFeatureFlagEvaluation(key, detail.value);
            },
          },
        ],
      }}
    >
      {children}
    </LDProvider>
  );
}
